import React, { type ChangeEvent, type ReactNode, useCallback, useState, useEffect } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import { LoadingButton } from '@atlaskit/button';
import Button from '@atlaskit/button/standard-button';
import { ErrorMessage, Field } from '@atlaskit/form';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import { SiteName } from '../../common/ui/site-name';
import { useThirdPartyState } from '../../controllers/import-state';
import messages from './messages';

type Props = {
	isOpen: boolean;
	title: string;
	description: ReactNode;
	onClose: () => void;
	cancelButton: {
		text: string;
		onClick: () => void;
	};
	submitButton: {
		text: string;
		onClick: (accessToken: string) => void;
	};
	isLoading?: boolean;
	hasErrorOccurred?: boolean;
	thirdParty: SupportedThirdParty;
	showCurrentAccount?: boolean;
};

const AccessTokenModal = ({
	isOpen,
	onClose,
	title,
	description,
	cancelButton,
	submitButton,
	isLoading,
	hasErrorOccurred,
	thirdParty,
	showCurrentAccount,
}: Props) => {
	const [accessToken, setAccessToken] = useState('');
	const [showError, setShowError] = useState(false);
	const [{ siteName }] = useThirdPartyState();

	useEffect(() => {
		setShowError(hasErrorOccurred === true);
	}, [hasErrorOccurred]);

	const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setAccessToken(e.target.value);
		setShowError(false);
	}, []);

	const onSubmit = useCallback(() => {
		submitButton.onClick(accessToken);
	}, [accessToken, submitButton]);

	const disabledButton = showError || isNil(accessToken) || accessToken === '';

	const { formatMessage } = useIntl();

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					shouldScrollInViewport={false}
					testId="project-create-importer-upload-flow.ui.request-access-token-modal.modal"
					shouldCloseOnEscapePress
					onClose={onClose}
				>
					<ModalHeader>
						<ModalTitle>{title}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						{description}
						<ContentWrapper>
							<Field
								label={formatMessage(messages.accessTokenTextfieldLabel)}
								name={formatMessage(messages.accessTokenTextfieldLabel)}
								isRequired
							>
								{({ fieldProps }) => (
									<>
										<TextField
											testId="project-create-importer-upload-flow.ui.request-access-token-modal.access-token-textfield"
											{...fieldProps}
											value={accessToken}
											onChange={onChange}
											isReadOnly={isLoading}
											isInvalid={showError}
											autoFocus
											placeholder={formatMessage(messages.accessTokenTextfieldPlaceholder, {
												thirdParty,
											})}
										/>
										{showError && (
											<ErrorMessage>
												{formatMessage(messages.accessTokenTextfieldErrorMessage)}
											</ErrorMessage>
										)}
									</>
								)}
							</Field>
						</ContentWrapper>
						{showCurrentAccount && siteName && <SiteName siteName={siteName} />}
					</ModalBody>
					<ModalFooter>
						<Button appearance="subtle" isDisabled={isLoading} onClick={cancelButton.onClick}>
							{cancelButton.text}
						</Button>
						<LoadingButton
							appearance="primary"
							isDisabled={disabledButton}
							isLoading={isLoading}
							onClick={onSubmit}
						>
							{submitButton.text}
						</LoadingButton>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};

export default AccessTokenModal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	margin: `${token('space.200', '16px')} 0`,
});
